// src/utils/safeLocalStorage.ts

const isBrowser =
  typeof window !== 'undefined' &&
  typeof window.localStorage !== 'undefined';

const safeGetRaw = (key: string): string | null => {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (err) {
    console.error(`safeLocalStorage.getItem error for key "${key}"`, err);
    return null;
  }
};

const safeSetRaw = (key: string, value: string): void => {
  if (!isBrowser) return; // silently no-op on server / build
  try {
    window.localStorage.setItem(key, value);
  } catch (err) {
    console.error(`safeLocalStorage.setItem error for key "${key}"`, err);
  }
};

const safeRemove = (key: string): void => {
  if (!isBrowser) return;
  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    console.error(`safeLocalStorage.removeItem error for key "${key}"`, err);
  }
};

export const safeLocalStorage = {
  /** Raw string get – returns null if missing or not available */
  get(key: string): string | null {
    return safeGetRaw(key);
  },

  /** Raw string set – no-op on server */
  set(key: string, value: string): void {
    safeSetRaw(key, value);
  },

  /** Remove – no-op on server */
  remove(key: string): void {
    safeRemove(key);
  },

  /** Get JSON value with a typed fallback */
  getJson<T>(key: string, fallback: T): T {
    const raw = safeGetRaw(key);
    if (raw == null) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch (err) {
      console.error(`safeLocalStorage.getJson parse error for key "${key}"`, err);
      return fallback;
    }
  },

  /** Set JSON value */
  setJson<T>(key: string, value: T): void {
    try {
      const raw = JSON.stringify(value);
      safeSetRaw(key, raw);
    } catch (err) {
      console.error(`safeLocalStorage.setJson stringify error for key "${key}"`, err);
    }
  },
};
